import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { ArrowLeft, Repeat } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import { useAuth } from '../../lib/auth';
import { GET_QUOTE } from '../../graphql/queries/quote';
import { CONVERT_QUOTE_TO_BOOKING } from '../../graphql/mutations/quote';
import { GET_PACKAGE_DETAILS } from '../../graphql/queries/package';
import { GET_AGENCY_PACKAGES } from '../../graphql/admin/queries/package';

export default function QuoteConvertPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const agencyName = (user as any)?.agencyName || (user as any)?.agency?.name;

  const [selectedPackageId, setSelectedPackageId] = useState<number | null>(null);
  const [selectedFormulaId, setSelectedFormulaId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: quoteData, loading: loadingQuote } = useQuery(GET_QUOTE, {
    variables: { id: Number(id) },
  });

  const { data: packagesData } = useQuery(GET_AGENCY_PACKAGES, {
    variables: { agencyName },
    skip: !agencyName,
  });

  const { data: packageDetails } = useQuery(GET_PACKAGE_DETAILS, {
    variables: { id: Number(selectedPackageId) },
    skip: !selectedPackageId,
  });

  const [convert, { loading: converting }] = useMutation(CONVERT_QUOTE_TO_BOOKING);

  if (loadingQuote || !quoteData?.quote) {
    return (
      <AdminLayout>
        <div className="p-8 text-center text-gray-500">Chargement du devis...</div>
      </AdminLayout>
    );
  }

  const quote = quoteData.quote;
  const packages = packagesData?.agencyPackages || [];
  const formulas = packageDetails?.package?.formulas || [];

  const handleConvert = async () => {
    setError(null);
    if (!selectedPackageId || !selectedFormulaId) {
      setError('Sélectionnez un package ET une formule.');
      return;
    }
    if (!quote.clientId) {
      setError(
        "Ce devis n'est pas rattaché à un client. Modifiez le devis et associez un client avant de convertir."
      );
      return;
    }
    try {
      const res = await convert({
        variables: {
          id: Number(id),
          packageId: selectedPackageId,
          formulaId: selectedFormulaId,
        },
      });
      const bookingId = res.data?.convertQuoteToBooking?.id;
      if (bookingId) {
        navigate(`/admin/bookings/edit/${bookingId}`);
      } else {
        navigate('/admin/bookings');
      }
    } catch (e: any) {
      setError(e.message || 'Erreur lors de la conversion.');
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-3xl">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(`/admin/quotes/${id}`)}
            className="text-gray-500 hover:text-gray-900"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-2xl font-bold">Convertir le devis en réservation</h1>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="mb-6 pb-6 border-b border-gray-200">
            <h2 className="font-semibold text-gray-900 mb-2">Devis source</h2>
            <div className="text-sm text-gray-700 space-y-1">
              <div>
                <span className="text-gray-500">Référence : </span>
                <span className="font-mono">{quote.reference}</span>
              </div>
              <div>
                <span className="text-gray-500">Client : </span>
                {quote.contactPrenom} {quote.contactNom} ({quote.contactEmail})
              </div>
              <div>
                <span className="text-gray-500">Titre : </span>
                {quote.title}
              </div>
              <div>
                <span className="text-gray-500">Montant total : </span>
                <strong>
                  {quote.totalPrice.toLocaleString('fr-FR')} {quote.currency}
                </strong>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Package associé <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedPackageId ?? ''}
                onChange={(e) => {
                  setSelectedPackageId(e.target.value ? Number(e.target.value) : null);
                  setSelectedFormulaId(null);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="">— Sélectionner un package —</option>
                {packages.map((p: any) => (
                  <option key={p.id} value={p.id}>
                    {p.title}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Formule <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedFormulaId ?? ''}
                onChange={(e) =>
                  setSelectedFormulaId(e.target.value ? Number(e.target.value) : null)
                }
                disabled={!selectedPackageId}
                className="w-full px-3 py-2 border border-gray-300 rounded-md disabled:bg-gray-100"
              >
                <option value="">
                  {selectedPackageId ? '— Sélectionner une formule —' : 'Choisissez d\'abord un package'}
                </option>
                {formulas.map((f: any) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))}
              </select>
            </div>

            {!quote.clientId && (
              <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 text-sm rounded-md p-3">
                ⚠ Ce devis n'est rattaché à aucun client. Rattachez-en un dans la fiche du
                devis avant la conversion.
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md p-3">
                {error}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
            <button
              onClick={() => navigate(`/admin/quotes/${id}`)}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm"
            >
              Annuler
            </button>
            <button
              onClick={handleConvert}
              disabled={converting || !selectedPackageId || !selectedFormulaId}
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50"
            >
              <Repeat className="w-4 h-4" />
              Convertir en réservation
            </button>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
